import { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/i18n';
import { useLanguageStore } from '@/stores/language.store';
import { DOMAIN_ORDER, DOMAIN_SPECS } from './domains';
import type { HrsnDomain } from '@/types/api';

/**
 * Cluster N v0.6.0 Fase 11.10 — Option C Hybrid stepper for the inline
 * sub-screening panel. Replaces the vertical 9-row Stepper used by
 * HouseholdScreeningPanel (Cluster H v2). The main agent ScreeningPage
 * still uses the original vertical Stepper.tsx — untouched.
 *
 * Visual approved by user via Dispatch mockup 2026-05-21 (F5-R1a: paleta
 * re-vestida a tokens del brand — misma estructura):
 *   - 40px circles for completed/pending domains; 44px for current with
 *     a 4px teal-tint ring (focus-style outer ring effect).
 *   - Completed: sage-tint background + check icon (positivo del brand).
 *   - Current: teal-filled + white text (interactivo del brand).
 *   - Pending: nav-soft background + nav-slate number text.
 *   - 2px connecting lines colored per the transition: sage between
 *     completed pairs, nav-card otherwise.
 * Below the stepper:
 *   - Metadata row "X de 9 completadas" (nav-slate) on the left + current
 *     section name (teal bold) on the right.
 * Below the question section (caller renders this):
 *   - Collapsed strip with chevron + "X secciones completadas" count +
 *     up-to-3 mini-chips (+N overflow). Click expands inline ABOVE the
 *     current question section so the agent can review prior findings
 *     without losing wizard context. AnimatePresence honors
 *     useReducedMotion() (drops the height animation to instant).
 *
 * No emojis. Honors Pathway palette only (no invented hex colors).
 */

interface HorizontalSubScreeningStepperProps {
  currentDomain: HrsnDomain;
  validDomains: ReadonlySet<HrsnDomain>;
  positiveDomains: ReadonlySet<HrsnDomain>;
  alertDomains?: ReadonlySet<HrsnDomain>;
  onSelect: (domain: HrsnDomain) => void;
}

export function HorizontalSubScreeningStepper({
  currentDomain,
  validDomains,
  positiveDomains,
  alertDomains,
  onSelect,
}: HorizontalSubScreeningStepperProps): JSX.Element {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const reducedMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(false);

  const currentIdx = DOMAIN_ORDER.indexOf(currentDomain);
  const completedCount = DOMAIN_ORDER.filter((d) => validDomains.has(d)).length;
  const completedDomains = DOMAIN_ORDER.filter((d) => validDomains.has(d) && d !== currentDomain);
  const currentSpec = DOMAIN_SPECS[currentDomain];
  const currentTitle = lang === 'es' ? currentSpec.titleEs : currentSpec.title;

  return (
    <div className="flex flex-col gap-3">
      {/* Horizontal stepper bar */}
      <nav aria-label={t('subScreeningStepper.ariaNav')} className="flex items-center w-full">
        {DOMAIN_ORDER.map((d, i) => {
          const isCurrent = d === currentDomain;
          const isDone = validDomains.has(d);
          const isAlert = alertDomains?.has(d) ?? false;
          const spec = DOMAIN_SPECS[d];
          const title = lang === 'es' ? spec.titleEs : spec.title;

          return (
            <div key={d} className="flex items-center" style={{ flex: i === DOMAIN_ORDER.length - 1 ? '0 0 auto' : '1 1 0' }}>
              <button
                type="button"
                onClick={() => onSelect(d)}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={t('subScreeningStepper.stepAria', { step: i + 1, title, status: isDone ? 'completed' : isCurrent ? 'current' : 'pending' })}
                title={title}
                className={cn(
                  'shrink-0 inline-flex items-center justify-center rounded-full font-semibold transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                  isCurrent
                    ? 'h-11 w-11 ring-4 ring-nav-tealTint bg-nav-teal text-white'
                    : isAlert
                      ? 'h-10 w-10 bg-nav-redTint text-nav-red'
                      : isDone
                        ? 'h-10 w-10 bg-nav-sageTint text-nav-sageText hover:bg-nav-sage'
                        : 'h-10 w-10 bg-nav-soft text-nav-slate hover:bg-nav-card',
                )}
              >
                {isAlert ? (
                  <AlertTriangle className="h-4 w-4" aria-hidden />
                ) : isDone && !isCurrent ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <span className="text-sm">{i + 1}</span>
                )}
              </button>
              {i < DOMAIN_ORDER.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'h-0.5 flex-1 mx-1',
                    // Connector color: mint completed→completed, lavender-light last-done→current, ink-light otherwise
                    isDone && validDomains.has(DOMAIN_ORDER[i + 1]!)
                      ? 'bg-nav-sage'
                      : isDone && DOMAIN_ORDER[i + 1] === currentDomain
                        ? 'bg-nav-tealTint'
                        : isCurrent && !validDomains.has(DOMAIN_ORDER[i + 1]!)
                          ? 'bg-nav-card'
                          : 'bg-nav-card',
                  )}
                />
              )}
            </div>
          );
        })}
      </nav>

      {/* Metadata row */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-nav-slate">
          {t('subScreeningStepper.completedOfTotal', { count: completedCount, total: DOMAIN_ORDER.length })}
        </span>
        <span className="font-semibold text-nav-tealDark">
          {currentTitle}
          <span className="ml-1 text-nav-faint font-normal">· {currentIdx + 1}/{DOMAIN_ORDER.length}</span>
        </span>
      </div>

      {/* Bottom collapsed strip — only render when there are completed sections (excluding current) */}
      {completedDomains.length > 0 && (
        <div className="rounded-md border border-nav-card bg-white">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-left',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal rounded-md',
              'hover:bg-nav-soft transition-colors',
            )}
            aria-expanded={expanded}
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 text-nav-slate transition-transform',
                expanded && 'rotate-180',
              )}
              aria-hidden
            />
            <span className="text-xs font-medium text-nav-navy">
              {t('subScreeningStepper.completedStrip.title', { count: completedDomains.length })}
            </span>
            {/* Up-to-3 chips preview when collapsed */}
            {!expanded && (
              <div className="flex items-center gap-1 ml-auto flex-wrap">
                {completedDomains.slice(0, 3).map((d) => {
                  const spec = DOMAIN_SPECS[d];
                  const title = lang === 'es' ? spec.titleEs : spec.title;
                  const isPositive = positiveDomains.has(d);
                  return (
                    <span
                      key={d}
                      className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border',
                        isPositive
                          ? 'bg-nav-amberTint text-nav-amberText border-nav-amber'
                          : 'bg-nav-sageTint text-nav-sageText border-nav-sage',
                      )}
                    >
                      <Check className="h-2.5 w-2.5" aria-hidden />
                      {title}
                    </span>
                  );
                })}
                {completedDomains.length > 3 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-nav-soft text-nav-navy">
                    +{completedDomains.length - 3}
                  </span>
                )}
              </div>
            )}
          </button>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="completed-list"
                initial={reducedMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto', transition: { duration: 0.2, ease: 'easeOut' } }}
                exit={reducedMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0, transition: { duration: 0.15 } }}
                className="overflow-hidden"
              >
                <div className="px-3 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {completedDomains.map((d) => {
                    const spec = DOMAIN_SPECS[d];
                    const title = lang === 'es' ? spec.titleEs : spec.title;
                    const isPositive = positiveDomains.has(d);
                    const isAlert = alertDomains?.has(d) ?? false;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          onSelect(d);
                          setExpanded(false);
                        }}
                        className={cn(
                          'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left',
                          'hover:bg-nav-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                          isAlert
                            ? 'border-nav-red bg-nav-redTint'
                            : isPositive
                              ? 'border-nav-amber bg-nav-amberTint'
                              : 'border-nav-sage bg-nav-sageTint',
                        )}
                      >
                        <span
                          className={cn(
                            'inline-flex h-6 w-6 items-center justify-center rounded-full shrink-0',
                            isAlert ? 'bg-nav-redTint text-nav-red'
                              : 'bg-nav-sageTint text-nav-sageText',
                          )}
                          aria-hidden
                        >
                          {isAlert ? <AlertTriangle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={cn(
                            'block text-xs font-medium truncate',
                            isAlert ? 'text-nav-red' : isPositive ? 'text-nav-amberText' : 'text-nav-sageText',
                          )}>
                            {title}
                          </span>
                          <span className={cn(
                            'block text-[10px]',
                            isAlert ? 'text-nav-red' : isPositive ? 'text-nav-amberText' : 'text-nav-sageMid',
                          )}>
                            {isAlert
                              ? t('subScreeningStepper.finding.alert')
                              : isPositive
                                ? t('subScreeningStepper.finding.positive')
                                : t('subScreeningStepper.finding.noFinding')}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
